import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';

const DAYS_TH = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
const MONTHS_TH = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
];

const pad = (n) => String(n).padStart(2, '0');
const toStr = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

// parse "YYYY-MM-DD" -> { y, m (0-indexed), d }
const parse = (str) => {
  if (!str || typeof str !== 'string') return null;
  const parts = str.split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  return { y: parts[0], m: parts[1] - 1, d: parts[2] };
};

const asNum = (p) => p.y * 10000 + p.m * 100 + p.d;

const CustomDatePicker = ({
  value,
  onChange,
  min,
  max,
  placeholder = 'เลือกวันที่',
  disabled = false,
  allowClear = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const containerRef = useRef(null);

  const selected = parse(value);
  const minP = parse(min);
  const maxP = parse(max);

  const now = new Date();
  const [viewYear, setViewYear] = useState(selected ? selected.y : now.getFullYear());
  const [viewMonth, setViewMonth] = useState(selected ? selected.m : now.getMonth());

  // Panel is w-72 (288px); flip to right-align when it would overflow the viewport
  useLayoutEffect(() => {
    if (!open || !containerRef.current) return;
    const decideAlignment = () => {
      const rect = containerRef.current.getBoundingClientRect();
      const panelWidth = Math.min(288, window.innerWidth - 16);
      const overflowsRight = rect.left + panelWidth > window.innerWidth - 8;
      const fitsWhenRightAligned = rect.right - panelWidth >= 8;
      setAlignRight(overflowsRight && fitsWhenRightAligned);
    };
    decideAlignment();
    window.addEventListener('resize', decideAlignment);
    return () => window.removeEventListener('resize', decideAlignment);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [open]);

  const handleToggle = () => {
    if (disabled) return;
    if (!open) {
      const base = selected || { y: now.getFullYear(), m: now.getMonth() };
      setViewYear(base.y);
      setViewMonth(base.m);
    }
    setOpen((o) => !o);
  };

  const isDisabledDay = (y, m, d) => {
    const n = y * 10000 + m * 100 + d;
    if (minP && n < asNum(minP)) return true;
    if (maxP && n > asNum(maxP)) return true;
    return false;
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const displayLabel = selected
    ? `${selected.d} ${MONTHS_TH[selected.m]} ${selected.y}`
    : placeholder;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={handleToggle}
        className={`w-full border rounded-lg px-3 py-2 flex items-center justify-between text-left transition-colors ${
          disabled
            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
            : open
              ? 'border-blue-500 bg-white'
              : 'border-gray-300 bg-white text-gray-800'
        }`}
        style={{ touchAction: 'manipulation' }}
      >
        <span className={`truncate ${!selected ? 'text-gray-400' : ''}`}>{displayLabel}</span>
        {allowClear && selected && !disabled ? (
          <span
            role="button"
            aria-label="ล้างวันที่"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
              setOpen(false);
            }}
            className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            ×
          </span>
        ) : (
          <svg className="w-5 h-5 ml-2 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )}
      </button>

      {open && !disabled && (
        <div
          className={`absolute z-40 mt-1 bg-white border-2 border-gray-200 rounded-2xl shadow-lg p-3 w-72 ${
            alignRight ? 'right-0' : 'left-0'
          }`}
          style={{ maxWidth: 'calc(100vw - 16px)' }}
        >
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={prevMonth}
              className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-lg font-bold hover:bg-blue-600"
              style={{ touchAction: 'manipulation' }}
            >
              ‹
            </button>
            <span className="font-semibold text-gray-800">
              {MONTHS_TH[viewMonth]} {viewYear}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-lg font-bold hover:bg-blue-600"
              style={{ touchAction: 'manipulation' }}
            >
              ›
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAYS_TH.map((d) => (
              <div key={d} className="text-center text-xs font-medium text-gray-500 py-1">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (d === null) return <div key={i} className="w-9 h-9" />;
              const dis = isDisabledDay(viewYear, viewMonth, d);
              const isSel =
                selected && selected.y === viewYear && selected.m === viewMonth && selected.d === d;
              return (
                <button
                  key={i}
                  type="button"
                  disabled={dis}
                  onClick={() => {
                    onChange(toStr(viewYear, viewMonth, d));
                    setOpen(false);
                  }}
                  className={`w-9 h-9 rounded-full text-sm font-medium transition-colors ${
                    isSel
                      ? 'bg-blue-600 text-white'
                      : dis
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-blue-100 active:bg-blue-200'
                  }`}
                  style={{ touchAction: 'manipulation' }}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;
